/**
 * API сервис для шаблонов карточек объектов и runtime-карточек.
 */

import { get, post, put, del } from "@/lib/api-client-v2";
import type {
  CardFieldWidgetTypeItem,
  CardTemplateCreate,
  CardTemplateImportOnConflict,
  CardTemplateImportResponse,
  CardTemplateListResponse,
  CardTemplatePublishRequest,
  CardTemplatePublishResponse,
  CardTemplateRestoreRequest,
  CardTemplateUpdate,
  CardTemplateWithDetails,
  ObjectCardResponse,
  ObjectCardUpdateRequest,
  ObjectCreateWithCardRequest,
} from "@/types/card-template";
import type {
  CardFieldRuleRow,
  CardFieldRulesPutRequest,
  TemplateRuleTypeMeta,
} from "@/types/template-rule-field-editor";
import type {
  ApplyRuleProfileToFieldsRequest,
  RuleProfile,
  RuleProfileListResponse,
} from "@/types/rule-profile";
import type { GeoObject } from "@/types/api-v2";
import {
  fetchCardTemplateCatalog,
  type CardTemplateCatalogListItem,
} from "@/services/card-template-catalog.service";
import type { CardTemplate } from "@/types/card-template";

/** Список таблицы конструктора: фасад GET /admin/card-template-catalog?module=oi (вариант B). */
function catalogOiItemsToCardTemplates(items: CardTemplateCatalogListItem[]): CardTemplate[] {
  return items.map(({ module: _m, ...row }) => {
    const created =
      row.created_at ??
      row.updated_at ??
      new Date(0).toISOString();
    return {
      id: row.id,
      code: row.code,
      name: row.name,
      description: row.description ?? null,
      object_kls_id: row.object_kls_id ?? null,
      organization_kls_id: row.organization_kls_id ?? null,
      is_default: row.is_default,
      status: row.status as CardTemplate["status"],
      version: row.version,
      is_active: row.is_active,
      created_by: row.created_by ?? null,
      created_at: created,
      updated_at: row.updated_at ?? null,
      published_at: row.published_at ?? null,
      archived_at: row.archived_at ?? null,
    };
  });
}

// ============================================================================
// Admin API — CRUD шаблонов
// ============================================================================

export const cardTemplatesApi = {
  /** Типы виджетов полей (узлы KLS qual FIELD_TYPES) */
  listFieldWidgetTypes: () =>
    get<CardFieldWidgetTypeItem[]>("/card-templates/meta/field-widget-types"),

  /** Каталог template_rule_types (редактор мета-правил поля) */
  listTemplateRuleTypes: (params?: { applies_to?: string; is_active?: boolean }) =>
    get<TemplateRuleTypeMeta[]>("/card-templates/meta/template-rule-types", params),

  getFieldRules: (templateId: number, fieldId: number) =>
    get<CardFieldRuleRow[]>(`/card-templates/${templateId}/fields/${fieldId}/field-rules`),

  putFieldRules: (templateId: number, fieldId: number, body: CardFieldRulesPutRequest) =>
    put<CardTemplateWithDetails>(
      `/card-templates/${templateId}/fields/${fieldId}/field-rules`,
      body
    ),

  getSectionRules: (templateId: number, sectionId: number) =>
    get<CardFieldRuleRow[]>(`/card-templates/${templateId}/sections/${sectionId}/section-rules`),

  putSectionRules: (templateId: number, sectionId: number, body: CardFieldRulesPutRequest) =>
    put<CardTemplateWithDetails>(
      `/card-templates/${templateId}/sections/${sectionId}/section-rules`,
      body
    ),

  getGeometrySpatialRules: (templateId: number, geometryRuleId: number) =>
    get<CardFieldRuleRow[]>(
      `/card-templates/${templateId}/geometry-rules/${geometryRuleId}/spatial-rules`
    ),

  putGeometrySpatialRules: (
    templateId: number,
    geometryRuleId: number,
    body: CardFieldRulesPutRequest
  ) =>
    put<CardTemplateWithDetails>(
      `/card-templates/${templateId}/geometry-rules/${geometryRuleId}/spatial-rules`,
      body
    ),

  /** JSON-пакет экспорта (schema geopulseml.card_template_export v1) */
  exportTemplateJson: (templateId: number) =>
    get<Record<string, unknown>>(`/card-templates/${templateId}/export`),

  /** Несколько шаблонов в одном JSON (до 50 id) */
  exportTemplatesBulk: (templateIds: number[]) =>
    post<Record<string, unknown>>("/card-templates/export", {
      template_ids: templateIds,
    }),

  /** Импорт JSON-пакета экспорта (создаются черновики) */
  importTemplatesJson: (
    bundle: Record<string, unknown>,
    onConflict: CardTemplateImportOnConflict = "error"
  ) => {
    const q = new URLSearchParams({ on_conflict: onConflict });
    return post<CardTemplateImportResponse>(`/card-templates/import?${q.toString()}`, bundle);
  },

  list: async (params?: {
    status?: string;
    object_kls_id?: number;
    /** Только шаблоны с object_kls_id = null (для всех типов объектов). */
    global_only?: boolean;
    is_active?: boolean;
    skip?: number;
    limit?: number;
  }): Promise<CardTemplateListResponse> => {
    const catalog = await fetchCardTemplateCatalog({
      module: "oi",
      status: params?.status,
      object_kls_id: params?.object_kls_id,
      global_only: params?.global_only,
      is_active: params?.is_active,
      skip: params?.skip,
      limit: params?.limit ?? 200,
    });
    if (!catalog.module_ready) {
      return {
        items: [],
        total: 0,
        skip: catalog.skip,
        limit: catalog.limit,
      };
    }
    return {
      items: catalogOiItemsToCardTemplates(catalog.items),
      total: catalog.total,
      skip: catalog.skip,
      limit: catalog.limit,
    };
  },

  get: (templateId: number) =>
    get<CardTemplateWithDetails>(`/card-templates/${templateId}`),

  create: (data: CardTemplateCreate) =>
    post<CardTemplateWithDetails>("/card-templates", data),

  update: (templateId: number, data: CardTemplateUpdate) =>
    put<CardTemplateWithDetails>(`/card-templates/${templateId}`, data),

  delete: (templateId: number) => del(`/card-templates/${templateId}`),

  publish: (templateId: number, data?: CardTemplatePublishRequest) =>
    post<CardTemplatePublishResponse>(
      `/card-templates/${templateId}/publish`,
      data ?? {}
    ),

  archive: (templateId: number, data?: CardTemplatePublishRequest) =>
    post<CardTemplatePublishResponse>(
      `/card-templates/${templateId}/archive`,
      data ?? {}
    ),

  restore: (templateId: number, data?: CardTemplateRestoreRequest) =>
    post<CardTemplatePublishResponse>(
      `/card-templates/${templateId}/restore`,
      data ?? {}
    ),

  clone: (templateId: number, newCode: string) =>
    post<CardTemplateWithDetails>(
      `/card-templates/${templateId}/clone?new_code=${encodeURIComponent(newCode)}`,
      {}
    ),

  /** Профили правил полей (фаза 6) */
  listRuleProfiles: (params?: { is_active?: boolean; skip?: number; limit?: number }) =>
    get<RuleProfileListResponse>("/card-templates/rule-profiles", params),

  getRuleProfile: (profileId: number) =>
    get<RuleProfile>(`/card-templates/rule-profiles/${profileId}`),

  applyRuleProfileToFields: (templateId: number, body: ApplyRuleProfileToFieldsRequest) =>
    post<CardTemplateWithDetails>(
      `/card-templates/${templateId}/fields/apply-rule-profile`,
      body
    ),
};

// ============================================================================
// Runtime API — карточка объекта
// ============================================================================

export const objectCardApi = {
  getCard: (objectId: number) =>
    get<ObjectCardResponse>(`/objects/${objectId}/card`),

  /** Черновик карточки для пары (тип объекта, организация) без объекта в БД */
  getCardDraft: (klsId: number, organizationId: number) =>
    get<ObjectCardResponse>("/objects/card-draft", {
      kls_id: klsId,
      organization_id: organizationId,
    }),

  updateCard: (objectId: number, payload: ObjectCardUpdateRequest) =>
    put<ObjectCardResponse>(`/objects/${objectId}/card`, payload),

  /** Создание объекта и применение карточки в одной транзакции */
  createWithCard: (payload: ObjectCreateWithCardRequest) =>
    post<GeoObject>("/objects/with-card", {
      ...payload,
      geometries: payload.geometries ?? [],
    }),
};
